import {ControllerFlowAPI} from '@wix/yoshi-flow-editor'
import {Reservee, Details} from '@wix/ambassador-table-reservations-v1-reservation/types'
import {ReservationLocation} from '@wix/ambassador-table-reservations-v1-reservation-location/types'
import {Checkout} from '@wix/ambassador-checkout/types'

import {reservationsService} from '../../../services/reservationsService'
import {RequestStatus} from '../../../utils/wrapRequest'
import {goToReservationConfirmation, navigateToCheckoutPage} from '../../../utils/navigation'
import {getLogger} from '../../../utils/getLogger'
import {checkoutService} from '../../../services/checkoutService'
import {trackTableReservedEvent} from '../../../utils/trackEvent'
import {EXPERIMENTS, TransactionNames} from '../../../utils/constants'
import {isWixHttpError} from '../../../utils/errors'
import {isFreeCheckout} from '../../../utils/ecom'

import {isReservationSuccessful, getRefererFromUrlParams} from './utils'

export const createReservation = (flowAPI: ControllerFlowAPI) => {
  return async (
    details: Details,
    reservee: Reservee,
    reservationLocation: ReservationLocation,
    paymentTokenizationSupported: boolean,
  ) => {
    const createReservationTransaction = flowAPI.panoramaClient.transaction(
      TransactionNames.createReservation,
    )
    const createCheckoutTransaction = flowAPI.panoramaClient.transaction(
      TransactionNames.createCheckout,
    )
    const createOrderTransaction = flowAPI.panoramaClient.transaction(TransactionNames.createOrder)

    createReservationTransaction.start({
      params: {
        details,
        reservee,
        reservationLocation,
        paymentTokenizationSupported,
      },
    })

    let checkoutResponse: {checkout?: Checkout}
    const setProps = flowAPI.controllerConfig.setProps
    const {getResolvedError} = flowAPI.errorHandler
    const logger = getLogger(flowAPI.bi!)
    const isBusinessLocationIdExperimentEnabled = flowAPI.experiments.enabled(
      EXPERIMENTS.businessLocationId,
    )
    const isErrorHandlerIntegrationEnabled = flowAPI.experiments.enabled(
      EXPERIMENTS.errorHandlerIntegration,
    )

    try {
      setProps({submitReservationStatus: RequestStatus.LOADING})

      const finishedReservation = await reservationsService.createReservation(flowAPI, {
        details,
        reservee,
      })

      // this is the only place where can get just created reservation
      if (finishedReservation?.id) {
        trackTableReservedEvent(flowAPI, finishedReservation.id, reservee)

        const referer = getRefererFromUrlParams(flowAPI)

        logger.reservationCreated({
          isPreview: false,
          reservation: finishedReservation,
          reservee,
          reservationLocation,
          isCardTokenization: paymentTokenizationSupported,
          referer,
        })

        const createCheckoutParams = {
          reservationId: finishedReservation.id,
          reservee,
          locationId: reservationLocation.location?.id ?? undefined,
        }

        createCheckoutTransaction.start({
          params: createCheckoutParams,
        })

        try {
          checkoutResponse = await checkoutService.createCheckout(
            flowAPI,
            createCheckoutParams,
            isBusinessLocationIdExperimentEnabled,
          )
        } catch (error: any) {
          flowAPI.panoramaClient.errorMonitor().reportError(error)

          getResolvedError(error)
          if (isReservationSuccessful(finishedReservation)) {
            console.error('Checkout is undefined')
            await goToReservationConfirmation({
              flowAPI,
              reservationId: finishedReservation.id,
            })

            return
          } else {
            throw error
          }
        }

        createCheckoutTransaction.finish({
          checkout: checkoutResponse,
        })

        if (!checkoutResponse?.checkout) {
          throw new Error('Checkout is undefined')
        }

        const isFreeReservation = isFreeCheckout(checkoutResponse.checkout)

        if (paymentTokenizationSupported && !isFreeReservation) {
          await navigateToCheckoutPage(
            flowAPI,
            checkoutResponse.checkout.id!,
            finishedReservation.id,
          )
        } else {
          let order

          const checkoutId = checkoutResponse.checkout.id!

          createOrderTransaction.start({
            params: {
              checkoutId,
            },
          })

          try {
            order = await checkoutService.createOrderFromCheckout(flowAPI, checkoutId)
          } catch (error: any) {
            flowAPI.panoramaClient.errorMonitor().reportError(error)

            if (isReservationSuccessful(finishedReservation)) {
              console.error('Error creating order from checkout', error)
            } else {
              throw error
            }
          }

          createOrderTransaction.finish({
            order,
          })

          await goToReservationConfirmation({
            flowAPI,
            reservationId: finishedReservation.id,
            isTemplateView: false,
            orderId: order?.orderId,
          })
        }
      } else {
        throw new Error('No reservation')
      }
    } catch (err: any) {
      flowAPI.panoramaClient.errorMonitor().reportError(err)

      const resolvedError = getResolvedError(err)
      if (isWixHttpError(err)) {
        setProps({
          submitReservationStatus: RequestStatus.FAILED,
          apiRequestErrorDetails: isErrorHandlerIntegrationEnabled
            ? resolvedError
            : {
                applicationCode: err?.response?.data?.details?.applicationError?.code,
                message: err?.response?.data?.message,
              },
        })
        return
      }

      setProps({
        submitReservationStatus: RequestStatus.FAILED,
        apiRequestErrorDetails: err,
      })
    }

    createReservationTransaction.finish()
  }
}
